import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { ArtifactType, ParentEntityType } from '@bower/shared';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useCreateArtifact } from '../data/useArtifacts';
import { useAppToast } from '../ui/useAppToast';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { getArtifactRoute } from '../../utils/artifactCapabilities';
import type { NoteCaptureMethod } from '../../constants/createActions';
import { destinationProblem } from '../../components/features/create/destination/createDestination';

export interface CreateAndNavigateTarget {
  readonly parentEntityType: ParentEntityType;
  /** Omit for the workspace root: the current workspace id is used. */
  readonly parentEntityId?: string;
}

/**
 * Create an empty artifact under a parent and open it. The note page then
 * auto-starts the capture (`useNoteAutoStart`). One implementation for every
 * create surface: the menus' items and the create modal's capture tiles.
 *
 * Resolves `true` once it has navigated, `false` on failure (already toasted).
 */
export function useCreateAndNavigate(
  surface: string,
  /**
   * Called instead of the error toast when the parent itself refused the
   * create (deleted, trashed, no longer writable): the create modal reopens
   * its picker. Absent, every failure toasts as before.
   */
  onDestinationProblem?: (problem: 'gone' | 'forbidden') => void,
) {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const toast = useAppToast();
  const createArtifact = useCreateArtifact();

  return useCallback(
    async (
      target: CreateAndNavigateTarget,
      artifactType: ArtifactType,
      captureMethod: NoteCaptureMethod,
      /** Runs with the new artifact's id just before its page opens. */
      onCreated?: (artifactId: string) => void,
    ): Promise<boolean> => {
      const parentEntityId = target.parentEntityId ?? workspace?.id;
      if (!workspace?.id || !parentEntityId) {
        toast({
          title: 'Workspace required',
          description: 'Please select a workspace first.',
          status: 'error',
        });
        return false;
      }
      try {
        const artifact = await createArtifact.mutateAsync({
          artifactType,
          associatedParent: { parentEntityType: target.parentEntityType, parentEntityId },
          title: '',
          content: { type: 'doc', content: [] },
          captureMethod,
          // No `captureSurface` here on purpose: the server derives it from
          // the `x-bower-client` header this client sends on every request. A
          // body value BEATS the header, so a copy here is not belt-and-braces
          // — it is the drift that left 56.6% of production artifacts with no
          // surface at all.
          metadata: { createdFrom: 'web_interface', creationSource: surface },
        });

        if (artifactType === 'protocol') {
          safeAnalytics.setPersonProperty('last_protocol_created_at', new Date().toISOString());
          safeAnalytics.track(ANALYTICS_EVENTS.PROTOCOL_CREATED, { creation_method: surface });
        } else {
          // image_to_text is reported as 'ocr', the creation_method the funnel uses.
          const creationMethod = captureMethod === 'image_to_text' ? 'ocr' : captureMethod;
          // The server owns Artifact Created; the client keeps only the
          // cross-surface content funnel. Protocol deliberately does not.
          safeAnalytics.track(ANALYTICS_EVENTS.CONTENT_CREATED, {
            content_type: 'note',
            creation_method: creationMethod,
          });
        }

        onCreated?.(artifact.id);
        const route = getArtifactRoute(artifactType, artifact.id);
        if (route) navigate(route);
        return true;
      } catch (error: unknown) {
        const problem = onDestinationProblem ? destinationProblem(error) : null;
        if (problem && onDestinationProblem) {
          onDestinationProblem(problem);
          return false;
        }
        const message =
          error instanceof Error ? error.message : `Could not create ${artifactType}.`;
        toast({ title: `Failed to create ${artifactType}`, description: message, status: 'error' });
        return false;
      }
    },
    [workspace, createArtifact, toast, navigate, surface, onDestinationProblem],
  );
}
